import axios from 'axios'
import {setupAxios} from '../app/modules/auth'
import {API_BASE_URL} from '../utils/contants'

// =================== PROJECT CODES ===================

export interface ProjectCode {
  id: string
  code: string
  name: string
  description?: string
  type: 'INTERNAL' | 'REAL'
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ProjectCodeInput {
  code: string
  name: string
  description?: string
  type: 'INTERNAL' | 'REAL'
  isActive?: boolean
}

export const getProjectCodes = async (filters?: {
  type?: string
  isActive?: boolean
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters?.type) params.append('type', filters.type)
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))

  return axios.get(`${API_BASE_URL}/project-codes?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getProjectCodeById = async (id: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/project-codes/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getProjectCodesDropdown = async () => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/project-codes/dropdown`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const createProjectCode = async (data: ProjectCodeInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/project-codes`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const updateProjectCode = async (id: string, data: Partial<ProjectCodeInput>) => {
  const {token} = setupAxios()
  return axios.put(`${API_BASE_URL}/project-codes/${id}`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const deleteProjectCode = async (id: string) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/project-codes/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const activateProjectCode = async (id: string) => {
  const {token} = setupAxios()
  return axios.patch(`${API_BASE_URL}/project-codes/${id}/activate`, {}, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== TOOLS ===================

export interface Tool {
  id: string
  name: string
  category?: string
  description?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface ToolInput {
  name: string
  category?: string
  description?: string
  isActive?: boolean
}

export const getTools = async (filters?: {category?: string; isActive?: boolean}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters?.category) params.append('category', filters.category)
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))

  return axios.get(`${API_BASE_URL}/tools?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getToolById = async (id: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tools/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getToolsDropdown = async () => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tools/dropdown`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getToolCategories = async () => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tools/categories`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getToolsByTaskType = async (taskTypeId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tools/by-task-type/${taskTypeId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getToolsByProject = async (projectCodeId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/tools/by-project/${projectCodeId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const createTool = async (data: ToolInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/tools`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const updateTool = async (id: string, data: Partial<ToolInput>) => {
  const {token} = setupAxios()
  return axios.put(`${API_BASE_URL}/tools/${id}`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const deleteTool = async (id: string) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/tools/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const activateTool = async (id: string) => {
  const {token} = setupAxios()
  return axios.patch(`${API_BASE_URL}/tools/${id}/activate`, {}, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== TASK TYPES ===================

export interface TaskType {
  id: string
  name: string
  category?: string
  description?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface TaskTypeInput {
  name: string
  category?: string
  description?: string
  isActive?: boolean
}

export const getTaskTypes = async (filters?: {category?: string; isActive?: boolean}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters?.category) params.append('category', filters.category)
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))

  return axios.get(`${API_BASE_URL}/task-types?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getTaskTypeById = async (id: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/task-types/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getTaskTypesDropdown = async () => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/task-types/dropdown`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getTaskTypeCategories = async () => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/task-types/categories`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const getTaskTypesByTool = async (toolId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/task-types/by-tool/${toolId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const createTaskType = async (data: TaskTypeInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/task-types`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const updateTaskType = async (id: string, data: Partial<TaskTypeInput>) => {
  const {token} = setupAxios()
  return axios.put(`${API_BASE_URL}/task-types/${id}`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const deleteTaskType = async (id: string) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/task-types/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

export const activateTaskType = async (id: string) => {
  const {token} = setupAxios()
  return axios.patch(`${API_BASE_URL}/task-types/${id}/activate`, {}, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== TIMESHEET ENTRIES ===================

export interface TimesheetEntry {
  id: string
  userId: string
  date: string
  projectCodeId: string
  taskId?: string | null
  toolId: string
  taskTypeId: string
  description: string
  billableHours: number
  actualHours: number
  isOvertime: boolean
  isWeekend: boolean
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'REVISION'
  submittedAt?: string | null
  approvedById?: string | null
  approvedAt?: string | null
  rejectedReason?: string | null
  managerRemarks?: string | null
  createdAt: string
  updatedAt: string
  // Relations
  user?: any
  projectCode?: ProjectCode
  task?: any
  tool?: Tool
  taskType?: TaskType
  approvedBy?: any
  reportingManager?: string;
}

export interface TimesheetEntryInput {
  userId: string
  date: string
  projectCodeId: string
  taskId?: string | null
  toolId: string
  taskTypeId: string
  description: string
  billableHours: number
  actualHours: number
  isOvertime?: boolean
  isWeekend?: boolean
}

export interface WeeklySummary {
  weekStart: string
  weekEnd: string
  entries: TimesheetEntry[]
  summary: {
    totalBillable: number
    totalActual: number
    totalOvertime: number
    totalWeekendHours: number
    entriesCount: number
    submittedCount: number
    approvedCount: number
    rejectedCount: number
    draftCount: number
  }
}

// Get all timesheet entries with filters
export const getTimesheetEntries = async (filters?: {
  userId?: string
  startDate?: string
  endDate?: string
  status?: string
  projectCodeId?: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters?.userId) params.append('userId', filters.userId)
  if (filters?.startDate) params.append('startDate', filters.startDate)
  if (filters?.endDate) params.append('endDate', filters.endDate)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.projectCodeId) params.append('projectCodeId', filters.projectCodeId)

  return axios.get(`${API_BASE_URL}/timesheet?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Get weekly timesheet
export const getWeeklyTimesheet = async (userId: string, date: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/timesheet/week/${date}?userId=${userId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Get single timesheet entry
export const getTimesheetEntry = async (id: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/timesheet/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Create timesheet entry
export const createTimesheetEntry = async (data: TimesheetEntryInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/timesheet`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Update timesheet entry (only DRAFT)
export const updateTimesheetEntry = async (id: string, data: Partial<TimesheetEntryInput>) => {
  const {token} = setupAxios()
  return axios.put(`${API_BASE_URL}/timesheet/${id}`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Delete timesheet entry (only DRAFT)
export const deleteTimesheetEntry = async (id: string) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/timesheet/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Submit daily timesheet
export const submitDailyTimesheet = async (userId: string, date: string) => {
  const {token} = setupAxios()
  return axios.post(
    `${API_BASE_URL}/timesheet/submit`,
    {userId, date},
    {
      headers: {Authorization: `Bearer ${token}`},
    }
  )
}

// Get user summary
export const getUserTimesheetSummary = async (
  userId: string,
  startDate: string,
  endDate: string
) => {
  const {token} = setupAxios()
  return axios.get(
    `${API_BASE_URL}/timesheet/summary/${userId}?startDate=${startDate}&endDate=${endDate}`,
    {
      headers: {Authorization: `Bearer ${token}`},
    }
  )
}

// =================== PHASE 6: MANAGER APPROVAL ===================

// Get pending approvals for a manager
export const getPendingApprovals = async (filters: {
  managerId: string
  startDate?: string
  endDate?: string
  userId?: string
  projectCodeId?: string
  status?: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  params.append('managerId', filters.managerId)
  if (filters.startDate) params.append('startDate', filters.startDate)
  if (filters.endDate) params.append('endDate', filters.endDate)
  if (filters.userId) params.append('userId', filters.userId)
  if (filters.projectCodeId) params.append('projectCodeId', filters.projectCodeId)
  if (filters.status) params.append('status', filters.status)

  return axios.get(`${API_BASE_URL}/timesheet/approvals/pending?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Approve a timesheet entry
export const approveTimesheetEntry = async (data: {
  entryId: string
  approvedById: string
  managerRemarks?: string
}) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/timesheet/approve`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Reject a timesheet entry
export const rejectTimesheetEntry = async (data: {
  entryId: string
  approvedById: string
  rejectedReason: string
  managerRemarks?: string
}) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/timesheet/reject`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// Bulk approve multiple entries
export const bulkApproveEntries = async (data: {
  entryIds: string[]
  approvedById: string
  managerRemarks?: string
}) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/timesheet/bulk-approve`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

